'use client'
import {
  PropsWithChildren,
  ReactNode,
  Ref,
  RefObject,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import SVGChevron from '@/src/components/common/SVG/SVGChevron'
import styles from './Accordion.module.scss'
import { classNames } from '@/src/helpers/component'
import useContainerResize from '@/src/hooks/useContainerResize'
import usePageLoad from '@/src/hooks/usePageLoad'
import useWindowScroll from '@/src/hooks/useWindowScroll'
import { getLocationHash } from '@/src/hooks/useHashChange'
import { CustomSVGWithDirection } from '@/src/components/common/SVG/SVG'
import { PageContext } from '@/src/contexts/PageContext'

type Chevrons = {
  chevronExpanded?: CustomSVGWithDirection['direction']
  chevronCollapsed?: CustomSVGWithDirection['direction']
}

interface ItemHandle {
  expanded: boolean | null
  toggle: (expanded: boolean) => void
}

interface ItemProps extends Chevrons {
  header: ReactNode
  content: ReactNode
  onToggle?: (arg0: boolean) => void
  ref?: Ref<ItemHandle>
  id?: string
}

type AccordionProps = {
  className?: string
  accordionControls?: boolean
  items: ItemProps[]
}

export function AccordionItem({
  id,
  ref,
  header,
  content,
  onToggle,
  chevronExpanded,
  chevronCollapsed,
}: PropsWithChildren<ItemProps>) {
  const [contentScrollHeight, setContentScrollHeight] = useState(0)
  const refContent = useRef<HTMLDivElement>(null)
  const updateScrollHeight = () => {
    setContentScrollHeight(refContent.current?.scrollHeight ?? 0)
  }

  const resizer = useContainerResize<HTMLDivElement>(updateScrollHeight)
  const refItem = useRef<HTMLDivElement>(null)

  // null = closed by default on page load -- automatically expanded on page load with hash / hash change
  // true/false = expanded/collapsed by user interaction -- no longer automatically expanded on page scroll
  const [expanded, setExpanded] = useState<boolean | null>(null)

  const hashTargetContainer = useCallback(() => {
    const container = refItem.current
    const hash = getLocationHash()
    if (!hash || container?.id !== hash) return
    return container
  }, [])

  const toggle = useCallback(
    (expanded: boolean) => {
      setExpanded(expanded)
      onToggle?.(expanded)
    },
    [onToggle],
  )

  useImperativeHandle(ref, () => ({ expanded, toggle }), [expanded, toggle])

  const hashHandler = () => {
    hashTargetContainer()?.scrollIntoView({ behavior: 'smooth' })
  }

  usePageLoad(hashHandler, 10)

  const pageContext = useContext(PageContext)

  useWindowScroll(() => {
    if (expanded !== null) return
    const container = hashTargetContainer()
    if (!container) return
    const containerTop = container.getBoundingClientRect().top
    if (containerTop >= 0 && containerTop <= 50 + (pageContext?.topnavHeightState[0] ?? 0)) {
      toggle(true)
    }
  })

  return (
    <div
      id={id ?? undefined}
      ref={refItem}
      className={classNames(styles.item, expanded ? styles.expanded : styles.collapsed)}
      onClick={e => {
        if (expanded) return
        e.stopPropagation()
        toggle(true)
      }}>
      <div
        onClick={() => toggle(!expanded)}
        className={styles.headerAndChevron}
        role="button"
        tabIndex={0}>
        {header}
        {chevronExpanded && chevronCollapsed && (
          <SVGChevron
            direction={expanded ? chevronExpanded : chevronCollapsed}
            className={classNames(styles.transition, styles.chevron)}
          />
        )}
      </div>
      <div
        ref={el => {
          if (!el) return
          refContent.current = el
          resizer.target(el)
        }}
        style={{ maxHeight: expanded ? contentScrollHeight : 0 }}
        className={classNames(styles.transition, styles.contentContainer)}>
        {content}
      </div>
    </div>
  )
}

function ToggleAllButton(_: { refItems: RefObject<(ItemHandle | null)[]>; expanded: boolean }) {
  return (
    <span
      onClick={() =>
        _.refItems.current.forEach(itemHandle => {
          itemHandle?.toggle(_.expanded)
        })
      }>
      {_.expanded ? 'Expand All' : 'Collapse All'}
    </span>
  )
}

export default function AccordionClient({ items, ..._ }: AccordionProps) {
  const refItems = useRef<(ItemHandle | null)[]>([])
  const [expandAll, setExpandAll] = useState(true)

  return (
    <div className={_.className}>
      {_.accordionControls && (
        <div className={styles.groupAction}>
          <ToggleAllButton refItems={refItems} expanded={expandAll} />
        </div>
      )}
      <div
        className={classNames(styles.accordion, _.className)}
        role="tablist"
        aria-multiselectable="true"
        tabIndex={0}>
        {items.map((item, idx) => (
          <AccordionItem
            {...item}
            key={idx}
            ref={el => {
              refItems.current[idx] = el
            }}
            onToggle={() => {
              setTimeout(
                () => setExpandAll(refItems.current.filter(_ => !_?.expanded).length > 0),
                100,
              )
            }}
          />
        ))}
      </div>
    </div>
  )
}
